import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../components/CustomButton";
import { Text } from "../../components/Text";
import CustomSkeleton from "../../components/CustomSkeleton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  createSupportTicket,
  getSupportTickets,
  setLimit,
  setPage,
} from "../../reducers/supportTicketSlice";
import CreateTicketModal from "./CreateTicketModal";

const HelpSupport = () => {
  const [success, setSuccess] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    list,
    totalCount,
    loadingList,
    creating,
    listError,
    createError,
    page,
    limit,
  } = useSelector((state) => state.supportTicket);

  useEffect(() => {
    dispatch(getSupportTickets({ page, limit }));
  }, [dispatch, page, limit]);

  const handleSubmit = (form) => {
    setSuccess(false);
    const payload = {
      subject: form.subject.trim(),
      description: form.description.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      status: "Open",
      priority: null,
      assignedTo: null,
      attachmentUrls: form.attachmentUrls || [],
    };
    dispatch(createSupportTicket(payload))
      .unwrap()
      .then(() => {
        setSuccess(true);
        dispatch(getSupportTickets({ page, limit }));
      })
      .catch(() => {});
  };

  const statusClass = (status) => {
    if (status === "Open")
      return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-700";
    if (status === "In Progress")
      return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700";
    return "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700";
  };
  const normalizeError = (err) => {
    const message = err?.message || err;
    if (typeof message !== "string") return "Something went wrong.";
    if (message.includes("<!DOCTYPE html>") || message.includes("Cannot GET")) {
      return "Unable to connect to support ticket service.";
    }
    return message;
  };

  return (
    <div className="pb-10">
      <div className="mt-4 border border-slate-200 rounded-lg bg-slate-50">
        <CreateTicketModal
          onSubmit={handleSubmit}
          submitting={creating}
          success={success}
          error={createError ? normalizeError(createError) : null}
        />
      </div>

      <div className="mt-4">
        {loadingList && (
          <CustomSkeleton
            parentcss="flex flex-column gap-2"
            countline={4}
            height="20px"
          />
        )}
        {!loadingList && list.length === 0 && (
          <div className="text-center p-6 text-slate-500">
            You haven't created any tickets yet.
          </div>
        )}
        {!loadingList && list.length > 0 && (
          <div className="card lg:my-2">
            <div className="w-full flex flex-column dashboard-tablecontent border-round-sm">
              <DataTable
                value={list}
                resizableColumns
                columnResizeMode="expand"
                className="skalebot-table"
                style={{ borderTop: "0px" }}
                paginator
                rows={limit}
                totalRecords={totalCount}
                first={page * limit}
                onPage={(e) => {
                  dispatch(setPage(e.page));
                  dispatch(setLimit(e.rows));
                }}
              >
                <Column
                  header={"SL No"}
                  style={{ width: "4rem" }}
                  body={(_, i) => (
                    <div className="font-normal">{i.rowIndex + 1}</div>
                  )}
                />
                <Column
                  field="id"
                  header={"Ticket ID"}
                  style={{ width: "10ch" }}
                  body={(rowData) => {
                    const rawId = rowData?.id;
                    const numericId =
                      typeof rawId === "number"
                        ? rawId
                        : Number.parseInt(rawId, 10);
                    const formattedId = Number.isFinite(numericId)
                      ? `SKLBT ${String(numericId).padStart(3, "0")}`
                      : "-";
                    return <div className="font-normal">{formattedId}</div>;
                  }}
                />
                <Column
                  field="subject"
                  header={"Subject"}
                  body={(rowData) => (
                    <div>
                      <div className="font-normal">{rowData.subject}</div>
                    </div>
                  )}
                />
                <Column
                  field="status"
                  header={"Status"}
                  body={(rowData) => (
                    <span className={statusClass(rowData.status)}>
                      {rowData.status}
                    </span>
                  )}
                  style={{ width: "12ch" }}
                />
                <Column
                  field="updatedAt"
                  header={"Updated At"}
                  body={(rowData) =>
                    rowData.updatedAt
                      ? new Date(rowData.updatedAt).toLocaleString()
                      : "-"
                  }
                  style={{ width: "18ch" }}
                />
                <Column
                  field="createdAt"
                  header={"Created At"}
                  body={(rowData) =>
                    rowData.createdAt || rowData.updatedAt
                      ? new Date(
                          rowData.createdAt || rowData.updatedAt,
                        ).toLocaleString()
                      : "-"
                  }
                  style={{ width: "18ch" }}
                />
                <Column
                  header={"Action"}
                  body={(rowData) => (
                    <div className="flex gap-2">
                      <CustomButton
                        varient="icon-button"
                        icon="pi pi-eye"
                        onClick={() =>
                          navigate(`/support/tickets/${rowData.id}`, {
                            state: { ticket: rowData },
                          })
                        }
                      />
                      <CustomButton varient="icon-button" icon="pi pi-pencil" />
                      <CustomButton
                        varient="icon-button"
                        icon="pi pi-comments"
                        onClick={() =>
                          navigate(`/support/tickets/${rowData.id}`, {
                            state: { ticket: rowData },
                          })
                        }
                      />
                    </div>
                  )}
                  style={{ width: "14ch" }}
                />
              </DataTable>
            </div>
          </div>
        )}
        {listError && !loadingList && (
          <div className="text-xs text-red-600 mt-2">
            {normalizeError(listError)}
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpSupport;
